import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import FileUploadInput, { FILE_ACCEPT_TYPES } from '../../components/admin/FileUploadInput';
import RichTextEditor from '../../components/admin/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Users, Save, Loader2, Plus, Trash2, Upload, X, Edit } from 'lucide-react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { getFileUrl } from '../../utils/assetUrlHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ForFarmersManagementPage() {
  const [activeTab, setActiveTab] = useState('agro-advisory');
  const [data, setData] = useState(null);
  const [successStories, setSuccessStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Agro Advisory
  const [weeklyPdfUrl, setWeeklyPdfUrl] = useState('');
  const [advisories, setAdvisories] = useState([]);
  const [uploadingWeeklyPdf, setUploadingWeeklyPdf] = useState(false);

  // Advisory dialog
  const [advisoryDialog, setAdvisoryDialog] = useState(false);
  const [editingAdvisory, setEditingAdvisory] = useState(null);
  const [advisoryForm, setAdvisoryForm] = useState({
    title: '',
    description: '',
    date: '',
    tags: [],
    pdf_file: '',
    is_latest: false
  });
  const [newTag, setNewTag] = useState('');

  // Plant Bookings
  const [plantBookings, setPlantBookings] = useState({
    how_to_order_steps: [],
    contact: { name: '', phone: '', email: '' },
    notes: ''
  });
  const [newStep, setNewStep] = useState('');

  // Success Stories
  const [storyDialog, setStoryDialog] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [storyForm, setStoryForm] = useState({
    title: '',
    overview: '',
    initiatives: '',
    outcome: '',
    impact: '',
    date: '',
    image: '',
    gallery_images: [],
    category: '',
    published: true
  });
  const [uploadingStoryImage, setUploadingStoryImage] = useState(false);

  // Unified File Data State
  const [fileData, setFileData] = useState({});

  useEffect(() => {
    fetchData();
    fetchSuccessStories();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cish_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/farmers/cms-data`, {
        headers: getAuthHeaders()
      });

      setData(response.data);
      setWeeklyPdfUrl(response.data.agro_advisory?.weekly_pdf_url || '');
      setAdvisories(response.data.agro_advisory?.advisories || []);
      setPlantBookings(response.data.plant_bookings || {
        how_to_order_steps: [],
        contact: { name: '', phone: '', email: '' },
        notes: ''
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuccessStories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/farmers/success-stories?published_only=false`);
      setSuccessStories(response.data);
    } catch (error) {
      console.error('Error fetching success stories:', error);
    }
  };

  const uploadSingleFile = async (file, subfolder) => {
    if (!file) return null;
    const formData = new FormData();
    formData.append('file', file);
    // Move subfolder to query parameter
    const res = await axios.post(`${API_URL}/api/files/upload?subfolder=${subfolder}`, formData, {
      headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
    });
    let url = res.data.file_path;
      if (url) {
        if (url.includes('data/files/')) url = url.replace('data/files/', '');
        if (!url.startsWith('/uploads/')) url = '/uploads/' + (url.startsWith('/') ? url.slice(1) : url);
      }
      return url;
  };

  const handleSaveMainData = async () => {
    try {
      setSaving(true);

      let currentWeeklyPdfUrl = weeklyPdfUrl;
      if (fileData.weekly_pdf) {
        const url = await uploadSingleFile(fileData.weekly_pdf, 'farmers');
        if (url) currentWeeklyPdfUrl = url;
      }

      await axios.put(
        `${API_URL}/api/farmers/cms-data`,
        {
          id: 'farmers-1',
          agro_advisory: {
            weekly_pdf_url: currentWeeklyPdfUrl,
            advisories: advisories
          },
          plant_bookings: plantBookings
        },
        { headers: getAuthHeaders() }
      );
      toast.success('Data saved successfully!');

      // Update local state if needed (though fetchData will refresh it)
      setWeeklyPdfUrl(currentWeeklyPdfUrl);
      setFileData(prev => ({ ...prev, weekly_pdf: null }));

      fetchData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Weekly PDF Upload
  const handleWeeklyPdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingWeeklyPdf(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/files/upload`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );

      setWeeklyPdfUrl(response.data.file_path);
      toast.success('Weekly PDF uploaded');
    } catch (error) {
      toast.error('Failed to upload PDF');
    } finally {
      setUploadingWeeklyPdf(false);
    }
  };

  // Advisory Management
  const handleAddAdvisory = () => {
    setEditingAdvisory(null);
    setAdvisoryForm({
      title: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      tags: [],
      pdf_file: '',
      is_latest: false
    });
    setAdvisoryDialog(true);
  };

  const handleEditAdvisory = (index) => {
    setEditingAdvisory(index);
    setAdvisoryForm({ ...advisories[index] });
    setAdvisoryDialog(true);
  };

  const handleSaveAdvisory = async () => {
    if (!advisoryForm.title.trim()) {
      toast.error('Title is required');
      return;
    }

    let finalPdfUrl = advisoryForm.pdf_file;
    if (fileData.advisory_pdf) {
      try {
        toast.info('Uploading PDF...');
        finalPdfUrl = await uploadSingleFile(fileData.advisory_pdf, 'advisories');
      } catch (e) {
        toast.error('Failed to upload PDF');
        return;
      }
    }

    if (!finalPdfUrl) {
      toast.error('PDF file is required');
      return;
    }

    const newAdvisory = { ...advisoryForm, pdf_file: finalPdfUrl };

    const newAdvisories = [...advisories];
    if (editingAdvisory !== null) {
      newAdvisories[editingAdvisory] = newAdvisory;
    } else {
      newAdvisories.push({ ...newAdvisory, id: `adv-${Date.now()}` });
    }

    setAdvisories(newAdvisories);
    setAdvisoryDialog(false);
    setFileData(prev => ({ ...prev, advisory_pdf: null })); // Clear
    toast.success('Advisory saved. Click "Save All Changes" to persist.');
  };

  const handleDeleteAdvisory = (index) => {
    if (!confirm('Delete this advisory?')) return;
    setAdvisories(advisories.filter((_, i) => i !== index));
    toast.success('Advisory removed. Click "Save All Changes" to persist.');
  };

  const addAdvisoryTag = () => {
    if (newTag.trim() && !advisoryForm.tags.includes(newTag.trim())) {
      setAdvisoryForm(prev => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag('');
    }
  };

  const removeAdvisoryTag = (tag) => {
    setAdvisoryForm(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  // Plant Bookings Management
  const addStep = () => {
    if (newStep.trim()) {
      setPlantBookings(prev => ({
        ...prev,
        how_to_order_steps: [...prev.how_to_order_steps, newStep.trim()]
      }));
      setNewStep('');
    }
  };

  const removeStep = (index) => {
    setPlantBookings(prev => ({
      ...prev,
      how_to_order_steps: prev.how_to_order_steps.filter((_, i) => i !== index)
    }));
  };

  // Success Story Management
  const handleAddStory = () => {
    setEditingStory(null);
    setStoryForm({
      title: '',
      overview: '',
      initiatives: '',
      outcome: '',
      impact: '',
      date: '',
      image: '',
      published: true
    });
    setFileData({});
    setStoryDialog(true);
  };

  const handleEditStory = (story) => {
    setEditingStory(story);
    setStoryForm({
      title: story.title,
      overview: story.overview,
      initiatives: story.initiatives,
      outcome: story.outcome,
      impact: story.impact,
      date: story.date || '',
      image: story.image || '',
      gallery_images: story.gallery_images || [],
      category: story.category || '',
      published: story.published
    });
    setFileData({});
    setFileData({});
    setStoryDialog(true);
  };

  const handleSaveStory = async () => {
    if (!storyForm.title.trim() || !storyForm.overview.trim()) {
      toast.error('Title and overview are required');
      return;
    }

    try {
      let finalImageUrl = storyForm.image;
      if (fileData.story_image) {
        finalImageUrl = await uploadSingleFile(fileData.story_image, 'success-stories');
      }
      
      let finalGallery = storyForm.gallery_images || [];
      if (fileData.story_gallery && fileData.story_gallery.length > 0) {
        const newGalleryUrls = await Promise.all(
             Array.from(fileData.story_gallery).map(file => uploadSingleFile(file, 'success-stories/gallery'))
        );
        finalGallery = [...finalGallery, ...newGalleryUrls];
      }

      const payload = { ...storyForm, image: finalImageUrl, gallery_images: finalGallery };

      if (editingStory) {
        await axios.put(
          `${API_URL}/api/farmers/success-stories/${editingStory.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Success story updated');
      } else {
        await axios.post(
          `${API_URL}/api/farmers/success-stories`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Success story created');
      }
      setStoryDialog(false);
      setFileData(prev => ({ ...prev, story_image: null, story_gallery: null }));
      fetchSuccessStories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save story');
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!confirm('Delete this success story?')) return;

    try {
      await axios.delete(
        `${API_URL}/api/farmers/success-stories/${storyId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Success story deleted');
      fetchSuccessStories();
    } catch (error) {
      toast.error('Failed to delete story');
    }
  };

  const handleStoryImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingStoryImage(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_URL}/api/files/upload`,
        formData,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
      );

      setStoryForm(prev => ({ ...prev, image: response.data.file_path }));
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingStoryImage(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0E6D2B]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-[#0E6D2B]" />
            <div>
              <h1 className="text-2xl font-bold text-gray-800">For Farmers Management</h1>
              <p className="text-sm text-gray-600">Manage Agro Advisory, Plant Bookings, and Success Stories</p>
            </div>
          </div>
          {activeTab !== 'success-stories' && (
            <Button onClick={handleSaveMainData} disabled={saving} className="bg-[#0E6D2B]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save All Changes
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid grid-cols-3 w-full max-w-2xl">
            <TabsTrigger value="agro-advisory">Agro Advisory</TabsTrigger>
            <TabsTrigger value="plant-bookings">Plant Bookings</TabsTrigger>
            <TabsTrigger value="success-stories">Success Stories</TabsTrigger>
          </TabsList>

          {/* AGRO ADVISORY TAB */}
          <TabsContent value="agro-advisory">
            <div className="space-y-6">
              {/* Weekly PDF */}
              <Card>
                <CardContent className="p-6">
                  <Label>Weekly PDF (Current Week)</Label>
                  <FileUploadInput
                    field={{ name: 'weekly_pdf', type: 'file', fileType: 'application/pdf' }}
                    value={fileData.weekly_pdf}
                    existingUrl={weeklyPdfUrl}
                    formData={{ weekly_pdf: weeklyPdfUrl }}
                    setFormData={() => { }} // Not strictly needed as we handle via state
                    onChange={(files) => setFileData({ ...fileData, weekly_pdf: files })}
                    onDeleteServerFile={() => { setWeeklyPdfUrl(''); return true; }}
                  />
                </CardContent>
              </Card>

              {/* Advisories */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Label>Advisories</Label>
                    <Button onClick={handleAddAdvisory} size="sm">
                      <Plus className="w-4 h-4 mr-1" /> Add Advisory
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {advisories.map((adv, index) => (
                      <div key={index} className="border rounded p-4 bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{adv.title}</p>
                            <p className="text-xs text-gray-500">{adv.date}</p>
                            {adv.is_latest && <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Latest</span>}
                          </div>
                          <div className="flex gap-2">
                            <Button onClick={() => handleEditAdvisory(index)} size="sm" variant="outline">
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button onClick={() => handleDeleteAdvisory(index)} size="sm" variant="destructive">
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 line-clamp-2" dangerouslySetInnerHTML={{ __html: adv.description }} />
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* PLANT BOOKINGS TAB */}
          <TabsContent value="plant-bookings">
            <Card>
              <CardContent className="p-6 space-y-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800">
                    ℹ️ <strong>Plants data</strong> is managed in <a href="/admin/services" className="underline">Services → Nursery</a>.
                    Here you manage ordering instructions and contact info.
                  </p>
                </div>

                {/* How to Order Steps */}
                <div>
                  <Label>How to Order Steps</Label>
                  <div className="flex gap-2 mt-2 mb-3">
                    <Input
                      value={newStep}
                      onChange={(e) => setNewStep(e.target.value)}
                      placeholder="Add step..."
                      onKeyPress={(e) => e.key === 'Enter' && addStep()}
                    />
                    <Button onClick={addStep} size="sm">Add</Button>
                  </div>
                  <ol className="list-decimal list-inside space-y-1">
                    {plantBookings.how_to_order_steps?.map((step, index) => (
                      <li key={index} className="flex justify-between items-center bg-gray-50 p-2 rounded">
                        <span className="text-sm">{step}</span>
                        <button onClick={() => removeStep(index)} className="text-red-500">
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ol>
                </div>

                {/* Contact Info */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Contact Name</Label>
                    <Input
                      value={plantBookings.contact?.name || ''}
                      onChange={(e) => setPlantBookings(prev => ({
                        ...prev,
                        contact: { ...prev.contact, name: e.target.value }
                      }))}
                      placeholder="Officer name"
                    />
                  </div>
                  <div>
                    <Label>Contact Phone</Label>
                    <Input
                      value={plantBookings.contact?.phone || ''}
                      onChange={(e) => setPlantBookings(prev => ({
                        ...prev,
                        contact: { ...prev.contact, phone: e.target.value }
                      }))}
                      placeholder="+91 1234567890"
                    />
                  </div>
                  <div>
                    <Label>Contact Email</Label>
                    <Input
                      value={plantBookings.contact?.email || ''}
                      onChange={(e) => setPlantBookings(prev => ({
                        ...prev,
                        contact: { ...prev.contact, email: e.target.value }
                      }))}
                      placeholder="nursery@cish.gov.in"
                    />
                  </div>
                </div>

                {/* Booking URL */}
                <div className="mb-4 mt-4">
                   <Label>Booking URL (Optional)</Label>
                   <Input
                     value={plantBookings.booking_url || ''}
                     onChange={(e) => setPlantBookings(prev => ({ ...prev, booking_url: e.target.value }))}
                     placeholder="https://..."
                   />
                </div>

                {/* Notes */}
                <div>
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={plantBookings.notes || ''}
                    onChange={(e) => setPlantBookings(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Additional information..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SUCCESS STORIES TAB */}
          <TabsContent value="success-stories">
            <div>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">{successStories.length} story(ies)</p>
                <Button onClick={handleAddStory} className="bg-[#0E6D2B]">
                  <Plus className="w-4 h-4 mr-2" /> Add Success Story
                </Button>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {successStories.map(story => (
                  <Card key={story.id}>
                    <CardContent className="p-4">
                      {story.image && <img src={getFileUrl(story.image)} alt={story.title} className="w-full h-32 object-cover rounded mb-3" />}
                      <p className="font-medium mb-1">{story.title}</p>
                      <p className="text-xs text-gray-500 mb-2">{story.date} • {story.slug}</p>
                      <div className="flex gap-2">
                        <Button onClick={() => handleEditStory(story)} size="sm" variant="outline" className="flex-1">
                          <Edit className="w-3 h-3" /> Edit
                        </Button>
                        <Button onClick={() => handleDeleteStory(story.id)} size="sm" variant="destructive" className="flex-1">
                          <Trash2 className="w-3 h-3" /> Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Advisory Dialog */}
        <Dialog open={advisoryDialog} onOpenChange={setAdvisoryDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAdvisory !== null ? 'Edit Advisory' : 'Add Advisory'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={advisoryForm.title}
                    onChange={(e) => setAdvisoryForm({ ...advisoryForm, title: e.target.value })}
                    placeholder="Advisory title"
                  />
                </div>
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={advisoryForm.date}
                    onChange={(e) => setAdvisoryForm({ ...advisoryForm, date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Description (Rich Text)</Label>
                <RichTextEditor
                  value={advisoryForm.description}
                  onChange={(value) => setAdvisoryForm(prev => ({ ...prev, description: value }))}
                  placeholder="Enter advisory details..."
                />
              </div>

              <div>
                <Label>PDF File * (Required)</Label>
                <FileUploadInput
                  field={{ name: 'advisory_pdf', type: 'file', fileType: 'application/pdf' }}
                  value={fileData.advisory_pdf}
                  existingUrl={advisoryForm.pdf_file}
                  formData={advisoryForm}
                  setFormData={setAdvisoryForm}
                  onChange={(files) => setFileData({ ...fileData, advisory_pdf: files })}
                  onDeleteServerFile={() => { setAdvisoryForm({ ...advisoryForm, pdf_file: '' }); return true; }}
                />
              </div>

              <div>
                <Label>Tags</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    placeholder="Add tag"
                    onKeyPress={(e) => e.key === 'Enter' && addAdvisoryTag()}
                  />
                  <Button onClick={addAdvisoryTag} size="sm" type="button">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {advisoryForm.tags.map(tag => (
                    <span key={tag} className="bg-gray-200 px-2 py-1 rounded text-sm flex items-center gap-1">
                      {tag}
                      <button onClick={() => removeAdvisoryTag(tag)} className="text-red-500">×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_latest"
                  checked={advisoryForm.is_latest}
                  onChange={(e) => setAdvisoryForm({ ...advisoryForm, is_latest: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_latest">Mark as Latest Advisory</Label>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button onClick={() => setAdvisoryDialog(false)} variant="outline">Cancel</Button>
                <Button onClick={handleSaveAdvisory} className="bg-[#0E6D2B]">
                  <Save className="w-4 h-4 mr-2" /> Save Advisory
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Success Story Dialog */}
        <Dialog open={storyDialog} onOpenChange={setStoryDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingStory ? 'Edit Success Story' : 'Add Success Story'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Title *</Label>
                  <Input
                    value={storyForm.title}
                    onChange={(e) => setStoryForm({ ...storyForm, title: e.target.value })}
                    placeholder="Success story title"
                  />
                </div>
                <div>
                  <Label>Date (Optional)</Label>
                  <Input
                    type="date"
                    value={storyForm.date}
                    onChange={(e) => setStoryForm({ ...storyForm, date: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <Label>Image (Optional)</Label>
                <FileUploadInput
                  field={{ name: 'story_image', type: 'file', fileType: 'image' }}
                  value={fileData.story_image}
                  existingUrl={storyForm.image}
                  formData={storyForm}
                  setFormData={setStoryForm}
                  onChange={(files) => setFileData({ ...fileData, story_image: files })}
                  onDeleteServerFile={() => { setStoryForm({ ...storyForm, image: '' }); return true; }}
                />
              </div>

              <div>
                <Label>Overview * (Rich Text)</Label>
                <RichTextEditor
                  value={storyForm.overview}
                  onChange={(value) => setStoryForm(prev => ({ ...prev, overview: value }))}
                  placeholder="Story overview..."
                />
              </div>

              <div>
                <Label>Initiatives (Rich Text)</Label>
                <RichTextEditor
                  value={storyForm.initiatives}
                  onChange={(value) => setStoryForm(prev => ({ ...prev, initiatives: value }))}
                  placeholder="Initiatives taken..."
                />
              </div>

              <div>
                <Label>Outcome (Rich Text)</Label>
                <RichTextEditor
                  value={storyForm.outcome}
                  onChange={(value) => setStoryForm(prev => ({ ...prev, outcome: value }))}
                  placeholder="Outcome achieved..."
                />
              </div>

              <div>
                <Label>Impact (Rich Text)</Label>
                <RichTextEditor
                  value={storyForm.impact}
                  onChange={(value) => setStoryForm(prev => ({ ...prev, impact: value }))}
                  placeholder="Impact on farmers..."
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="story_published"
                  checked={storyForm.published}
                  onChange={(e) => setStoryForm({ ...storyForm, published: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="story_published">Published</Label>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button onClick={() => setStoryDialog(false)} variant="outline">Cancel</Button>
                <Button onClick={handleSaveStory} className="bg-[#0E6D2B]">
                  <Save className="w-4 h-4 mr-2" /> Save Story
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
